import React from "react";
import { assertNever } from "../../amorg-common";
import { colors } from "../colors/color";
import styled from "styled-components";

type PopupMenuCustomButtonProps = {
    entries:
        (
            { type: 'separator' } |
            { type: 'label', text: string } |
            { type: 'menuItem', content: React.ReactNode, onClick?: () => void }
        )[],
    buttonContent: React.ReactNode,
    disabled?: boolean,
    horizontalAlignment?: 'left' | 'right',
    hoverEffect?: 'none' | 'background' | 'border',
}
export function PopupMenuCustomButton(props: PopupMenuCustomButtonProps): JSX.Element
{
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        function handleMouseDown(event: MouseEvent)
        {
            if (rootRef.current && !rootRef.current.contains(event.target as Node))
            {
                setOpen(false);
            }
        }

        function handleEscape(event: KeyboardEvent)
        {
            if (event.key === 'Escape')
            {
                setOpen(false);
            }
        }

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    function renderEntry(entry: PopupMenuCustomButtonProps['entries'][number], index: number): JSX.Element
    {
        if (entry.type === 'separator')
        {
            return <div key={`separator-${index}`} style={{ width: '100%', borderTop: `1px solid ${colors.divider.default}` }} />;
        }

        if (entry.type === 'label')
        {
            return (
                <div key={`label-${index}`} style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', color: colors.text.light }}>
                    {entry.text}
                </div>
            );
        }

        if (entry.type === 'menuItem')
        {
            return (
                <MenuItemButton
                    key={`item-${index}`}
                    onClick={() => {
                        setOpen(false);
                        entry.onClick?.();
                    }}>

                    {entry.content}
                    
                </MenuItemButton>
            );
        }

        return assertNever(entry);
    }

    const hAlignment = props.horizontalAlignment ?? 'right';

    return (
        <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
            <LaunchButton 
                hoverEffect={props.hoverEffect ?? 'background'}
                disabled={props.disabled} 
                onClick={() => setOpen(current => !current)}>
                {props.buttonContent}
            </LaunchButton>

            {open &&
                <div
                    style={{
                        position: 'absolute',
                        right: hAlignment === 'right' ? 0 : 'auto',
                        left: hAlignment === 'left' ? 0 : 'auto',
                        top: 'calc(100% + 4px)',
                        minWidth: 160,
                        border: `1px solid ${colors.menu.border}`,
                        borderRadius: 4,
                        background: colors.menu.background,
                        padding: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 4,
                        zIndex: 1000
                    }}
                >
                    {props.entries.map((entry, index) => renderEntry(entry, index))}
                </div>
            }
        </div>
    );
}

const LaunchButton = styled.button<{hoverEffect: 'none' | 'background' | 'border'}>`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 4px 8px;
    background: none;
    border: 1px solid rgba(0, 0, 0, 0);
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    color: ${props => props.theme.colors.text.normal};
    cursor: pointer;
    text-align: left;
    white-space: nowrap;
    transition: all 0.5s ease;

    &:hover {
        ${props => props.hoverEffect === 'background' && `background: ${props.theme.colors.popupMenuCustomButton.backgroundHover};`}
        ${props => props.hoverEffect === 'border' && `border: 1px solid ${props.theme.colors.popupMenuCustomButton.borderHover};`}
    }

    &:disabled {
        color: ${props => props.theme.colors.text.muted};
        cursor: not-allowed;
    }
`;

const MenuItemButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 4px 8px;
    background: none;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    color: ${props => props.theme.colors.text.normal};
    cursor: pointer;
    text-align: left;
    white-space: nowrap;

    &:hover {
        background: ${props => props.theme.colors.button.text.backgroundHover};
    }

    &:disabled {
        color: ${props => props.theme.colors.text.muted};
        cursor: not-allowed;
    }
`;

